import { forwardRef } from 'react';
import { css } from '@emotion/react';
import { LayoutMode_Enum } from '@weave/schema-gen-ts/dist/schemas/phone/devices/v2/devices.pb';
import { LineKeysTypes } from '@frontend/api-line-keys';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, styles, Text } from '@frontend/design-system';
import { useLineKeyState } from '../store/line-key-provider';

export const DeviceLayoutTouchScreen = forwardRef<
  HTMLDivElement,
  {
    isPreviewEnabled?: boolean;
    lineKeys: LineKeysTypes.GetLineKeysType['output']['lineKeys'] | undefined;
    model: LineKeysTypes.ModelInfoType | undefined;
    device: LineKeysTypes.ListDeviceType | undefined;
    pageNum: number;
    touchScreenMode: LayoutMode_Enum;
    onToggleMode: () => void;
  }
>(({ isPreviewEnabled, lineKeys, model, device, pageNum, touchScreenMode, onToggleMode }, ref) => {
  const isCollapsedMode = touchScreenMode === LayoutMode_Enum.COLLAPSED;

  let { scrollToCard } = useLineKeyState(['scrollToCard']);
  if (isPreviewEnabled) {
    scrollToCard = () => {};
  }

  const currentPageIndex = pageNum - 1;
  const pageMeta =
    touchScreenMode === LayoutMode_Enum.COLLAPSED
      ? model?.touchKeys?.pages.find((p) => p.mode === LayoutMode_Enum.COLLAPSED)
      : model?.touchKeys?.pages.filter((p) => p.mode === LayoutMode_Enum.EXPANDED)[currentPageIndex];

  const keys = Object.keys(pageMeta?.keyPositions ?? {});
  const columns = model?.touchKeys?.columns ?? 0;
  const rows = model?.touchKeys?.rows ?? 0;
  const extension = device?.sipProfiles?.map((ext) => ext.name)?.[0];

  // An array with the column indexes to hide. It will be any columns except the first and last.
  // Example, if there are a total of 4 columns, then the columnsToHide will be [2, 3]
  const columnsToHide: number[] = new Array(model?.touchKeys?.columns ?? 0)
    .fill(undefined)
    .map((_, i) => i + 1)
    .filter((_, i, arr) => i + 1 !== 1 && i + 1 !== arr.length);

  if (!model?.touchKeys) return null;

  return (
    <>
      <div
        ref={ref}
        className='touch-screen-container'
        css={[gridStyles({ columns, rows }), touchScreenStyles(device?.model)]}
      >
        {/* Hardcoded collapse button for T87W model */}
        {device?.model === 't87w' && !isCollapsedMode && (
          <Button
            css={css`
              position: absolute;
              top: ${theme.spacing(0.5)};
              right: ${theme.spacing(1)};
            `}
            iconName='x'
            onClick={onToggleMode}
            variant='secondary'
          />
        )}

        {keys.map((keyString, i) => {
          // The index of this key in the keyPositions object. The index starts at 1 not 0.
          const keyPositionIndex = Number(keyString);
          // The actual number to display for the key. We subtract 1 from the keyPositionIndex to make up for the slot
          // of the extension which takes up the first slot.
          // NOTE: The 0 key will be handled as an extension and will not be displayed as a regular key.
          const keyNumber = keyPositionIndex - 1;
          // The index of the associated line key data in the lineKeys array. Since the lineKeys array is 0-indexed
          // and the keyNumber is 1-indexed, we need to subtract 1 from the keyNumber.
          const lineKeyIndex = keyNumber - 1;

          const gridPosition = pageMeta?.keyPositions[keyPositionIndex];
          const isFirstSlot = keyNumber === 0;
          const keyExists = !!lineKeys?.[lineKeyIndex];
          const nextIsAvailable = (lineKeys?.length ?? 0) + 1 === i; // We add plus 1 to account for the extension
          const canBeHidden = gridPosition && columnsToHide.includes(gridPosition?.column);

          return (
            <div
              key={keyString}
              className={
                `touch-screen-tile` +
                ` ${isFirstSlot ? 'static' : ''}` +
                ` ${keyExists || nextIsAvailable ? 'active' : 'inactive'}` +
                ` ${isPreviewEnabled ? 'preview' : ''}` +
                ` ${canBeHidden && isCollapsedMode ? 'hidden' : ''}`
              }
              css={gridItemStyles({ position: gridPosition })}
              onClick={() => {
                if (isPreviewEnabled) {
                  return;
                }

                if (keyExists || nextIsAvailable) {
                  scrollToCard(keyNumber);
                }
              }}
            >
              {isFirstSlot && (
                // These keys will be the first keys on a page (if they exist on that page)
                <Text weight='bold' className='touch-screen-tile-name' as='span'>
                  {extension}
                </Text>
              )}

              {!isFirstSlot && (
                // This is the number of the touch key
                <Text weight='bold' size='medium' className='touch-screen-tile-key' as='span'>
                  {keyNumber}
                </Text>
              )}
              {(!!keyExists || !!nextIsAvailable) && (
                <>
                  {/* This is the text inside the touch key */}
                  <Text weight='bold' size='medium' className='touch-screen-tile-name' css={styles.truncate} as='span'>
                    {lineKeys?.[lineKeyIndex]?.name}
                  </Text>
                </>
              )}
            </div>
          );
        })}

        {pageMeta?.modeKeyPosition && pageMeta.modeKeyLabel && (
          <div
            className={`touch-screen-tile active ${isPreviewEnabled ? 'preview' : ''}`}
            css={gridItemStyles({ position: pageMeta?.modeKeyPosition })}
            onClick={onToggleMode}
          >
            {device?.model === 't87w' && isCollapsedMode ? (
              // Hardcoded expand key for T87W model
              <span
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 100%;
                  height: 100%;
                `}
              >
                <Icon name='expand' />
              </span>
            ) : (
              <Text weight='bold' size='medium' className='touch-screen-tile-key' as='span'>
                {pageMeta?.modeKeyLabel}
              </Text>
            )}
          </div>
        )}
      </div>
    </>
  );
});
DeviceLayoutTouchScreen.displayName = 'DeviceLayoutTouchScreen';

const gridStyles = ({ columns, rows }: { columns: number; rows: number }) => css`
  display: grid;
  grid-template-columns: repeat(${columns}, 1fr);
  grid-template-rows: repeat(${rows}, 1fr);
  gap: ${theme.spacing(1)};
`;

const gridItemStyles = ({ position }: { position?: { column: number; row: number } }) => {
  return css`
    grid-column: ${position?.column};
    grid-row: ${position?.row};
  `;
};

const touchScreenStyles = (model?: string) => css`
  width: calc(100% - ${theme.spacing(7)});
  background: ${theme.colors.neutral10};
  margin: auto;
  padding: ${theme.spacing(5, 2)};
  border-radius: ${theme.borderRadius.large};
  border: 2px solid ${theme.colors.neutral90};
  overflow-x: auto;
  position: relative;
  justify-items: center;

  .touch-screen-tile {
    border: 2px solid ${theme.colors.neutral30};
    border-radius: ${theme.borderRadius.medium};
    background: ${theme.colors.white};
    padding: ${theme.spacing(1)};
    width: ${theme.spacing(14)};
    height: ${theme.spacing(7.5)};
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1.5)};

    ${model === 't87w' &&
    css`
      padding: ${theme.spacing(1, 0.75)};
      width: 150px;
    `}
  }

  .touch-screen-tile-key {
    color: ${theme.colors.neutral50};
  }

  .touch-screen-tile-name {
    color: ${theme.colors.neutral90};
  }

  .touch-screen-tile.static {
    border: 2px solid ${theme.colors.neutral20};
  }

  .touch-screen-tile.active:not(.static, .preview):hover {
    background: ${theme.colors.neutral5};
    color: ${theme.colors.neutral50};
    cursor: pointer;
  }

  .touch-screen-tile.active:not(.static, .preview):active {
    background: ${theme.colors.neutral10};
    color: ${theme.colors.neutral50};
  }

  .touch-screen-tile.static .touch-screen-tile-name {
    color: ${theme.colors.neutral30};
  }

  .touch-screen-tile.inactive {
    border-color: transparent;
  }

  .touch-screen-tile.inactive .touch-screen-tile-key {
    color: ${theme.colors.neutral20};
  }

  .touch-screen-tile.hidden {
    display: none;
  }
`;
